import { shallowEqual } from 'react-redux'
import { createSelectorCreator } from 'reselect'

/**
 * Create memoized function that maintains infinite entries by entity ID (second parameter)
 * and 1 entry for state (first parameter).
 *
 * When state changes, the entity cache is cleared.
 *
 * This is very specific memoize designed for selectors which _last_ argument is some entity ID.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoizeByEntityId<FuncT extends (...args: any[]) => any>(func: FuncT): FuncT {
  const entries = new Map<unknown, ReturnType<FuncT>>()
  let lastState: unknown = undefined
  return ((...args: unknown[]) => {
    if (args.length < 2) {
      throw new Error('memoizeByEntityId requires at least 2 arguments')
    }
    const entityId = args.pop()
    if (!shallowEqual(lastState, args)) {
      entries.clear()
      lastState = args
    }

    if (entries.has(entityId)) {
      return entries.get(entityId) as ReturnType<FuncT>
    }
    const result = func(...args, entityId)
    entries.set(entityId, result as ReturnType<FuncT>)
    return result as ReturnType<FuncT>
  }) as FuncT
}

/**
 * Create selector that memoizes by entity ID (last argument) and state (initial arguments).
 *
 * See problem description: https://reselect.js.org/api/weakMapMemoize.
 *
 * Example usage:
 * ```
 *    const fooBarSelector = createEntitySelector(
 *        (state) => state.foo, // if changes, clear cache
 *        (state) => state.bar, // if changes, clear cache
 *        (_, entityId) => entityId, // cache by this _within_ same state of above selectors
 *        (foo, bar, entityId) => { ... foo[entityId], ...bar[entityId] }
 *    )
 *
 *    fooBarSelector(state, 1) // calls selector(state, 1)
 *    fooBarSelector(state, 1) // returns cached value
 *    fooBarSelector(state, 2) // calls selector(state, 2)
 *    fooBarSelector(state, 1) // returns cached value
 *    // only state change will clear cache
 * ```
 *
 */
export const createEntitySelector = createSelectorCreator(memoizeByEntityId)
